import type { Subscription } from 'dexie'
import { liveQuery } from 'dexie'
import { useRoute, useRouter } from 'vue-router'
import { onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useConfirmDialog } from './confirmDialog'
import { useUserStore } from '@/store/userData'
import appConfig from '@/services/appConfig'
import type MyArticle from '@/models/myArticle'
import utils from '@/services/utils'
import { useArticleStore } from '@/store/article'
import type ModelDetails from '@/modules/articleDetails/ModelDetails.vue'
import type ArticleDetails from '@/modules/articleDetails/ArticleDetails.vue'

export default function useArticleDetails(moduleName = 'Catalog') {
  const { t } = useI18n()
  const userStore = useUserStore()
  const confirmDialog = useConfirmDialog()
  const route = useRoute()
  const router = useRouter()
  const articleStore = useArticleStore()

  let clickedArticleSubscription: Subscription | null = null
  const articleDetailsOpen = ref(false)
  const clickedArticle = ref<MyArticle | null>(null)
  let selectedArticleOnMerchOrWhiteboardForSwap: number | null = null
  let reLoad = false

  const modelDetailsRef = ref<InstanceType<typeof ModelDetails>>()
  const articleDetailsRef = ref<InstanceType<typeof ArticleDetails>>()

  function onArticleUpdated(key: string, value: any) {
    if (clickedArticle.value && clickedArticle.value.hasOwnProperty(key)) {
      clickedArticle.value[key] = value
    }
  }

  function onChangeArticle(art: MyArticle) {
    if (art) {
      clickedArticle.value = art
    }
  }

  function onArticleClick(art: MyArticle, reload = false, isClickedFromMerchOrWhiteboard = false) {
    if (isClickedFromMerchOrWhiteboard) {
      selectedArticleOnMerchOrWhiteboardForSwap = art.Id
    }
    reLoad = reload
    if (reload) {
      articleDetailsOpen.value = false
    }

    const newParams: { articleId: string, objectId?: string, linkedCatalogCode?: number, reload?: number } = { articleId: art.Id.toString(), objectId: undefined, linkedCatalogCode: undefined, reload: undefined }
    if (art._IsRequestArticle) {
      newParams.objectId = art.CatalogArticleId
    }
    if (userStore.activeCatalog && art.CatalogCode !== userStore.activeCatalog.CatalogCode) {
      newParams.linkedCatalogCode = art.CatalogCode
    }
    const mergedParams = { ...router.currentRoute.value.params, ...newParams }
    router.push({ name: `${moduleName}ArticleDetails`, params: mergedParams, query: { ...router.currentRoute.value.query }, state: { selectedArticleOnMerchOrWhiteboardForSwap } })

    if (articleDetailsOpen.value) {
      clickedArticle.value = art
    }
  }

  async function openArticleDetails() {
    if (!articleDetailsOpen.value && appConfig.DB && userStore.activeCatalog && userStore.myAttributes && route.params.articleId) {
      // TODO: Add loading
      let catalogDetails = userStore.activeCatalog
      const articleId = Number(route.params.articleId)
      const linkedCatalogCode = Number(route.params.linkedCatalogCode)
      if (linkedCatalogCode && userStore.linkedCatalogDetails[linkedCatalogCode]) {
        catalogDetails = userStore.linkedCatalogDetails[linkedCatalogCode]
      }
      if (clickedArticleSubscription) {
        clickedArticleSubscription.unsubscribe()
        clickedArticleSubscription = null
      }
      const objectId = route.params.objectId?.toString()
      if (utils.isValidStringValue(objectId)) {
        if (catalogDetails.CatalogCode === userStore.activeCatalog.CatalogCode) {
          const obs = liveQuery(async () => await appConfig.DB!.getRequestArticleById(catalogDetails, articleId, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet))
          clickedArticleSubscription = obs.subscribe({
            next: async (requestArticle) => {
              if (requestArticle) {
                clickedArticle.value = requestArticle
                articleDetailsOpen.value = true
              }
            },
            error: err => console.error('Error while retrieving requests', err),
          })
        }
        else {
          console.warn('this should never happen')
        }
      }
      else {
        const res = await appConfig.DB.getMyArticlesByIds(catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes, userStore.currentUsername, userStore.userProfile.Id, [articleId], userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
        if (res.length) {
          articleStore.updateArticleBulk(res)
          articleDetailsOpen.value = true
          clickedArticle.value = res[0]
        }
      }
    }
  }

  async function closeArticleDetails() {
    let dialogResult = 'confirm'
    if (clickedArticle.value) {
      if ((modelDetailsRef.value && modelDetailsRef.value.isFormDirty())) {
        articleDetailsOpen.value = true
        dialogResult = await confirmDialog(t('updateArticle.youWantToLeaveThePage'), t('updateArticle.youSaveYourChangesDiscardContinue'), [], undefined, async () => {
          if (appConfig.DB) {
            await appConfig.DB.articleForms.delete([clickedArticle.value!.CatalogCode, clickedArticle.value!.Id, userStore.userProfile.Id, 'modelDetails'])
          }
        }, 'general.save', undefined, true)
      }
      else if (articleDetailsRef.value && articleDetailsRef.value.isUpdateFormDirty()) {
        articleDetailsOpen.value = true
        dialogResult = await confirmDialog(t('updateArticle.youWantToLeaveThePage'), t('updateArticle.youSaveYourChangesDiscardContinue'), [], undefined, async () => {
          if (appConfig.DB) {
            await appConfig.DB.articleForms.delete([clickedArticle.value!.CatalogCode, clickedArticle.value!.Id, userStore.userProfile.Id, 'editArticle'])
          }
        }, 'general.save', undefined, true)
      }
    }
    if (dialogResult !== 'cancel') {
      articleDetailsOpen.value = false
      clickedArticle.value = null
      selectedArticleOnMerchOrWhiteboardForSwap = null
      if (clickedArticleSubscription) {
        clickedArticleSubscription.unsubscribe()
        clickedArticleSubscription = null
      }
      if (route.name !== moduleName && !reLoad) {
        const newParams = { ...router.currentRoute.value.params }
        delete newParams.articleId
        router.push({ name: moduleName, params: newParams, query: { ...router.currentRoute.value.query } })
      }
      if (reLoad) {
        reLoad = false
      }
    }
  }

  onBeforeUnmount(() => {
    if (clickedArticleSubscription) {
      clickedArticleSubscription.unsubscribe()
      clickedArticleSubscription = null
    }
  })

  return {
    articleDetailsOpen,
    modelDetailsRef,
    articleDetailsRef,
    clickedArticle,
    onArticleUpdated,
    onArticleClick,
    openArticleDetails,
    closeArticleDetails,
    onChangeArticle,
  }
}
